import React, { useEffect, useState } from 'react'
import { Button, ConfigProvider, Form, Input, Modal, Radio, Select } from 'antd'
import { useTranslation } from 'react-i18next'
import { FormButton } from '../group.styled'
import { useSelector } from 'react-redux'
import { addUserRole, fetchRoleList } from '../../../apis/role'
import { addBrandUser } from '../../../apis/brand'
import { addUser, fetchUserList } from '../../../apis/user'
import { addGroupUser } from '../../../apis/group'

function AddUserModal (props) {
  const { show, setShow, successCallback = () => {}, adminMode, groupsOption, brandsOption, data, groupMode } = props
  const { t: ln_t } = useTranslation()
  const [form] = Form.useForm()
  const user = useSelector((state) => state.user)
  const [isLoading, setIsLoading] = useState(false)
  const [roleOptions, setRoleOptions] = useState(false)
  const [accountExist, setAccountExist] = useState(false)
  const [brandOptions, setBrandOptions] = useState([])
  const joinedGroup = data?.groups?.map(i => i.id)

  const defPlaceholder = (name, type) => {
    const message = type === 'input' ? ln_t('util.pleaseEnter') : type === 'select' ? ln_t('util.pelaseSelect') : ''
    return `${message} ${ln_t(`user.${name}`)}`
  }
  const defConfig = (name, type, required = true) => ({ name, label: ln_t(`user.${name}`), rules: [{ required, message: defPlaceholder(name, type) }] })
  const statusOptions = [{ value: 1, label: ln_t('util.enable') }, { value: 0, label: ln_t('util.disable') }]

  const handleClose = () => {
    setShow(false)
    setAccountExist(false)
    form.resetFields()
  }

  const handleSave = async value => {
    setIsLoading(true)
    const { email, name, groupId, groupUserRoleIds, brandId, role } = value || {}
    let userId = groupMode ? data?.id : accountExist
    if (!accountExist && !groupMode) {
      const res = await addUser({ email, name, role: 'member' })
      if (res?.status === 200) userId = res?.data?.id
    }
    if (!userId) return setIsLoading(false)
    const addUserToGroup = await addGroupUser({ userId, groupId })
    if (addUserToGroup?.status !== 201) return setIsLoading(false)
    const addUserGroupRole = await addUserRole({ userId, groupId, groupUserRoleIds })
    if (addUserGroupRole?.status !== 200) return setIsLoading(false)
    const addUserToBrand = await addBrandUser({ userId, brandId, role })
    if (addUserToBrand?.status !== 201) return setIsLoading(false)
    successCallback(data?.id)
    handleClose()
    setIsLoading(false)
  }

  const checkAccount = async e => {
    const email = e.target.value
    if (!email || !email.includes('@')) return setAccountExist(false)
    const check = await fetchUserList({ email, role: 'member' })

    if (check?.status === 200) return setAccountExist(check?.data?.length ? check?.data?.[0]?.id : false)
    setAccountExist(false)
  }

  const getRoleOptions = async groupId => {
    const res = await fetchRoleList({ groupId })
    if (res?.status === 200) setRoleOptions(res?.data?.map(i => ({ value: i.id, label: i.name })))
  }

  const handleChangeGroup = e => {
    if (!adminMode) return
    getRoleOptions(e)
    setBrandOptions(brandsOption.filter(i => i.groupId === e))
    form.setFieldsValue({ groupUserRoleIds: undefined, brand: undefined, brandRole: undefined })
  }

  useEffect(() => {
    if (!show) return
    if (groupMode && data) form.setFieldsValue({ name: data?.name, email: data?.email })
    if (adminMode) return
    setBrandOptions(brandsOption)
    form.setFieldValue('groupId', user?.nowGroup)
    getRoleOptions(user?.nowGroup)
  }, [show])

  const emailWarning = accountExist ? { validateStatus: 'warning', help: ln_t('message.info.accountExist') } : {}
  const nameWarning = accountExist ? { validateStatus: 'warning', help: ln_t('message.info.nameWillBeIgnored'), rules: [{ required: false }] } : {}
  return (
    <ConfigProvider theme={{ token: { colorPrimary: 'rgb(71, 182, 175)', colorPrimaryHover: 'rgba(71, 182, 175,.8)' } }}>
      <Modal title={groupMode ? ln_t('user.addToGroup') : `${ln_t('util.add')} ${ln_t('user.user')}`} open={show} onCancel={handleClose} footer={null} width={800}>
        <Form form={form} layout="vertical" requiredMark={false} onFinish={e => handleSave(e)} style={{ marginTop: '16px' }}>
          <div style={{ display: 'flex', gap: '8px' }}>
            <div style={{ width: '50%' }}>
              <Form.Item {...defConfig('email', 'input')} {...emailWarning}rules={[{ required: true, message: defPlaceholder('email', 'input') }, { type: 'email', message: ln_t('login.PleaseEnterTheCorrectFormat') }]}>
                <Input placeholder={defPlaceholder('email', 'input')} onChange={e => checkAccount(e)} disabled={groupMode} />
              </Form.Item>
              <Form.Item {...defConfig('group', 'select')} name="groupId">
                <Select
                  showSearch
                  placeholder={defPlaceholder('group', 'select')}
                  options={groupMode ? groupsOption?.map(i => ({ ...i, disabled: joinedGroup?.includes(i.value) })) : groupsOption}
                  onChange={e => handleChangeGroup(e)}
                  disabled={!adminMode}
                  optionFilterProp="children"/>
              </Form.Item>
              <Form.Item {...defConfig('brand', 'select')} name="brandId">
                <Select showSearch placeholder={defPlaceholder('brand', 'select')} options={brandOptions} optionFilterProp="children"/>
              </Form.Item>
              <Form.Item {...defConfig('status', 'select')} initialValue={1}>
                <Radio.Group options={statusOptions} optionType="button" buttonStyle="solid"/>
              </Form.Item>
            </div>
            <div style={{ width: '50%' }}>
              <Form.Item {...defConfig('name', 'input')} {...nameWarning}>
                <Input placeholder={defPlaceholder('name', 'input')} disabled={groupMode} />
              </Form.Item>
              <Form.Item {...defConfig('groupRole', 'select')} name="groupUserRoleIds">
                <Select showSearch mode="multiple" placeholder={defPlaceholder('groupRole', 'select')} options={roleOptions} optionFilterProp="children"/>
              </Form.Item>
              {/* <Form.Item {...defConfig('brandRole', 'select')} name="role">
                <Select showSearch placeholder={defPlaceholder('brandRole', 'select')} options={brandRoleOptions} optionFilterProp="children"/>
              </Form.Item> */}
            </div>
          </div>
          <FormButton>
            <Button type="default" onClick={handleClose}>{ln_t('util.cancel')}</Button>
            <Button type="primary" htmlType="submit" loading={isLoading}>{ln_t('util.confirm')}</Button>
          </FormButton>
        </Form>
      </Modal>
    </ConfigProvider>
  )
}

export default AddUserModal
